import type {
	CustomerInfo,
	PurchasesOfferings,
	PurchasesPackage,
} from "@revenuecat/purchases-capacitor";
import { LOG_LEVEL, Purchases } from "@revenuecat/purchases-capacitor";

/**
 * RevenueCat Native Service
 * Handles RevenueCat operations for iOS/Android platforms (App Store/Play Store)
 */

let isConfigured = false;

/**
 * Configure Native SDK (iOS/Android)
 */
export async function configure(
	platform: string,
	userId?: string,
): Promise<void> {
	if (isConfigured) {
		return;
	}

	await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG });

	const apiKey =
		platform === "ios"
			? import.meta.env.VITE_REVENUECAT_IOS_API_KEY
			: import.meta.env.VITE_REVENUECAT_ANDROID_API_KEY;

	if (!apiKey) {
		throw new Error(`RevenueCat API key not found for platform: ${platform}`);
	}

	await Purchases.configure({
		apiKey,
		appUserID: userId,
	});

	isConfigured = true;
	console.log(`[RevenueCat Native] Configured for ${platform}`);
}

/**
 * Get available offerings
 */
export async function getOfferings(): Promise<PurchasesOfferings> {
	if (!isConfigured) {
		throw new Error("RevenueCat Native SDK not configured");
	}

	const offerings = await Purchases.getOfferings();
	return offerings;
}

/**
 * Purchase a package via App Store/Play Store
 */
export async function purchasePackage(
	packageToPurchase: PurchasesPackage,
): Promise<{ customerInfo: CustomerInfo; success: boolean }> {
	if (!isConfigured) {
		throw new Error("RevenueCat Native SDK not configured");
	}

	const result = await Purchases.purchasePackage({
		aPackage: packageToPurchase,
	});

	console.log("[RevenueCat Native] Purchase successful");
	return { customerInfo: result.customerInfo, success: true };
}

/**
 * Get customer info
 */
export async function getCustomerInfo(): Promise<CustomerInfo> {
	if (!isConfigured) {
		throw new Error("RevenueCat Native SDK not configured");
	}

	const result = await Purchases.getCustomerInfo();
	return result.customerInfo;
}

/**
 * Check if user has active subscription
 */
export async function hasActiveSubscription(): Promise<boolean> {
	try {
		const customerInfo = await getCustomerInfo();
		const activeEntitlements = Object.keys(
			customerInfo.entitlements.active || {},
		);
		return activeEntitlements.length > 0;
	} catch (error) {
		console.error(
			"[RevenueCat Native] Failed to check subscription status:",
			error,
		);
		return false;
	}
}

/**
 * Restore previous purchases
 */
export async function restorePurchases(): Promise<CustomerInfo> {
	if (!isConfigured) {
		throw new Error("RevenueCat Native SDK not configured");
	}

	const result = await Purchases.restorePurchases();
	console.log("[RevenueCat Native] Purchases restored successfully");
	return result.customerInfo;
}

/**
 * Set user ID (log in)
 */
export async function setUserId(userId: string): Promise<void> {
	if (!isConfigured) {
		throw new Error("RevenueCat Native SDK not configured");
	}

	await Purchases.logIn({ appUserID: userId });
	console.log("[RevenueCat Native] User ID set successfully");
}

/**
 * Log out user
 */
export async function logOut(): Promise<void> {
	if (!isConfigured) {
		throw new Error("RevenueCat Native SDK not configured");
	}

	await Purchases.logOut();
	isConfigured = false;
	console.log("[RevenueCat Native] User logged out successfully");
}

/**
 * Check if SDK is configured
 */
export function getIsConfigured(): boolean {
	return isConfigured;
}

/**
 * Get subscription management URL
 * Returns the URL where users can manage/cancel their subscription
 */
export async function getManagementURL(): Promise<string | null> {
	if (!isConfigured) {
		throw new Error("RevenueCat Native SDK not configured");
	}

	const customerInfo = await getCustomerInfo();
	return customerInfo.managementURL || null;
}
